import React, { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "../lib/api";
import { Button, Input, Card, Badge } from "../components/ui";
import ContentManager from "../components/ContentManager";
import TranslationHealth from "../components/TranslationHealth";
import { Mail, Users, Ban, ArrowDownUp, Send, Trash2, Copy, Languages, Inbox } from "lucide-react";

const LISTS = [
  { key: "newsletter", label: "FIDUX™ subscribers", icon: Mail },
  { key: "contact", label: "Contact enquiries", icon: Users },
  { key: "do_not_contact", label: "Do-not-contact", icon: Ban },
  { key: "messages", label: "Contact messages", icon: Inbox },
];

export default function Audiences() {
  const [list, setList] = useState("newsletter");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState(-1);
  const [data, setData] = useState({ items: [], count: 0, duplicates: 0 });
  const [form, setForm] = useState({ name: "", email: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState({ items: [], total: 0, main_count: 0, main_by_code: {}, boxes: [] });
  const [msgFilter, setMsgFilter] = useState("");
  const [newCount, setNewCount] = useState(0);
  const [boxView, setBoxView] = useState("main");

  const SEEN_KEY = "fopfo_msgs_seen_at";

  const load = useCallback(async () => {
    if (list === "messages") {
      try { const { data } = await client.get("/site/admin/contact-messages", { params: { box: boxView } }); setMessages(data); }
      catch (e) { setMsg(apiErr(e)); }
      return;
    }
    try {
      const { data } = await client.get("/site/audience", { params: { list, sort, order } });
      setData(data);
    } catch (e) { setMsg(apiErr(e)); }
  }, [list, sort, order, boxView]);

  useEffect(() => { load(); }, [load]);

  // New-message badge: count un-routed (main-box) messages received since the admin last opened the inbox.
  useEffect(() => {
    client.get("/site/admin/contact-messages", { params: { box: "main" } }).then((r) => {
      const items = r.data.items || [];
      const seen = localStorage.getItem(SEEN_KEY) || "";
      setNewCount(items.filter((m) => (m.created_at || "") > seen).length);
    }).catch(() => {});
  }, []);

  // Opening the inbox marks everything seen.
  useEffect(() => {
    if (list === "messages") { localStorage.setItem(SEEN_KEY, new Date().toISOString()); setNewCount(0); }
  }, [list]);

  const routeMessages = async () => {
    if (!window.confirm("Execute the routing rule now? All un-routed messages that have a subject code will move into their topic boxes.")) return;
    try { const { data } = await client.post("/site/admin/contact-messages/route"); setMsg(`Routed ${data.routed} message(s) into their topic boxes.`); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };

  const delMessage = async (id) => {
    if (!window.confirm("Delete this contact message?")) return;
    try { await client.delete(`/site/admin/contact-messages/${id}`); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };

  const add = async () => {
    if (!form.name || !form.email) { setMsg("Enter a name and email."); return; }
    try { await client.post("/site/audience", { ...form, list }); setForm({ name: "", email: "" }); setMsg("Added."); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Remove this record?")) return; await client.delete(`/site/audience/${id}`); load(); };
  const unsub = async (r) => {
    if (!window.confirm(`Unsubscribe ${r.email} from FIDUX?`)) return;
    try { await client.post("/site/newsletter/unsubscribe", { name: r.name || "", email: r.email }); setMsg(`${r.email} unsubscribed.`); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };
  const dedupe = async () => {
    setBusy(true);
    try { const { data } = await client.post("/site/audience/dedupe", null, { params: { list } }); setMsg(`Removed ${data.removed} duplicate(s).`); load(); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };
  const sendAll = async () => {
    if (!window.confirm("Send all blog posts and newsletter issues to gcainfo@simprime-ca.com for scheduling?")) return;
    setBusy(true);
    try { const { data } = await client.post("/site/send-all-content"); setMsg(`Sent ${data.blog_posts} posts + ${data.newsletter_issues} issues to ${data.to}.`); }
    catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };
  const warmTranslations = async () => {
    setBusy(true); setMsg("Preparing…");
    // Shared session_id groups all 14-lang calls into ONE ledger row so the admin cost
    // meter reads "one warm across all languages" instead of 14 per-language rows.
    const sid = `admin-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      const { data: plan } = await client.post("/site/landing/warm");
      const langs = plan.languages || [];
      let done = 0, failed = [];
      for (const code of langs) {
        setMsg(`Re-warming all languages… ${done}/${langs.length} (${code}) — site chrome, Solutions, blogs & newsletters`);
        try {
          await client.post(`/site/landing/warm?lang=${code}&session_id=${sid}`);
          await client.post(`/site/solutions/warm?lang=${code}`);
          await client.post(`/site/warm-content?lang=${code}`);
        } catch { failed.push(code); }
        done += 1;
      }
      setMsg(`Re-warmed ${langs.length - failed.length}/${langs.length} language(s): site chrome, Solutions letter, blogs & newsletters all cached.${failed.length ? ` Retry needed: ${failed.join(", ")}.` : " Unchanged items were served from cache at no cost."}`);
    } catch (e) { setMsg(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="audiences-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-head text-3xl">FIDUX™ &amp; Contacts</h1>
          <p className="text-sm opacity-60">Manage subscriber, contact and do-not-contact lists.</p>
        </div>
        <Button variant="subtle" className="ms-auto" onClick={warmTranslations} disabled={busy} data-testid="warm-translations"><Languages size={15} /> Re-warm all languages</Button>
        <Button onClick={sendAll} disabled={busy} data-testid="send-all-content"><Send size={15} /> Send all content now</Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {LISTS.map((l) => (
          <button key={l.key} onClick={() => setList(l.key)} data-testid={`audience-tab-${l.key}`}
            className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm lift ${list === l.key ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>
            <l.icon size={15} /> {l.label}
            {l.key === "messages" && newCount > 0 && (
              <span data-testid="messages-new-badge"
                className="ms-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-[#DA1E28] px-1.5 text-[11px] font-semibold text-white">{newCount}</span>
            )}
          </button>
        ))}
      </div>

      {list === "messages" ? (
        <Card>
          {(() => {
            const isMain = boxView === "main";
            const shown = messages.items.filter((m) => !isMain || !msgFilter || m.subject_short === msgFilter);
            return (
              <>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Inbox size={15} className="opacity-60" />
                    <select value={boxView} onChange={(e) => { setBoxView(e.target.value); setMsgFilter(""); }} data-testid="messages-box-select"
                      className="rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1.5 text-sm">
                      <option value="main">📥 Main inbox — arrivals ({messages.main_count})</option>
                      {(messages.boxes || []).map((b) => <option key={b.code} value={b.code}>{b.label} ({b.code}) — {b.count}</option>)}
                    </select>
                  </label>
                  {isMain && (
                    <Button onClick={routeMessages} data-testid="messages-route" className="ms-auto">
                      <ArrowDownUp size={15} /> Execute routing rule
                    </Button>
                  )}
                </div>

                {isMain ? (
                  <p className="mb-3 text-xs opacity-60">
                    New messages land here first. Review the mix below, then <b>Execute routing rule</b> to move them into their per-topic boxes.
                  </p>
                ) : (
                  <p className="mb-3 text-xs opacity-60">Topic box: <b>{(messages.boxes.find((b) => b.code === boxView) || {}).label}</b> ({boxView}). Assign language specialists here later.</p>
                )}

                {isMain && Object.keys(messages.main_by_code || {}).length > 0 && (
                  <div className="mb-4 flex flex-wrap gap-2" data-testid="main-code-summary">
                    {Object.entries(messages.main_by_code).sort((a, b) => b[1] - a[1]).map(([code, n]) => (
                      <button key={code} type="button" onClick={() => setMsgFilter(msgFilter === code ? "" : code)}
                        data-testid={`main-code-chip-${code}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${msgFilter === code ? "border-[var(--primary)] bg-[var(--primary)]/10 font-medium" : "border-black/15 dark:border-white/15 hover:bg-black/5 dark:hover:bg-white/5"}`}>
                        <span className="font-semibold">{code}</span> × {n}
                      </button>
                    ))}
                  </div>
                )}

                <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
                  <span data-testid="messages-count">{shown.length} message(s){isMain && messages.main_count !== shown.length ? ` of ${messages.main_count}` : ""}</span>
                </div>
                {msg && <p className="mb-3 text-sm opacity-70" data-testid="audience-msg">{msg}</p>}

                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="messages-table">
                    <thead className="text-start opacity-60">
                      <tr className="border-b border-black/10 dark:border-white/10">
                        <th className="py-2 pe-4">Subject</th><th className="py-2 pe-4">From</th>
                        <th className="py-2 pe-4">Message</th><th className="py-2 pe-4">Received</th><th className="py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((m) => (
                        <tr key={m.id} className="border-b border-black/5 dark:border-white/5 align-top" data-testid={`message-row-${m.id}`}>
                          <td className="py-2 pe-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{m.subject_display || m.subject}</span>
                              {m.subject_short && <Badge color="info" data-testid={`message-code-${m.id}`}>{m.subject_short}</Badge>}
                            </div>
                            {m.lang && m.lang !== "ENG" && <span className="text-xs opacity-50">{m.lang}</span>}
                          </td>
                          <td className="py-2 pe-4">
                            <div>{`${m.first_name || ""} ${m.last_name || ""}`.trim() || "—"}</div>
                            <div className="text-xs opacity-60">{m.email}</div>
                          </td>
                          <td className="py-2 pe-4 max-w-md opacity-80">{m.comment || <span className="opacity-40">—</span>}</td>
                          <td className="py-2 pe-4 opacity-60 whitespace-nowrap">{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                          <td className="py-2 text-end">
                            <button onClick={() => delMessage(m.id)} data-testid={`message-del-${m.id}`} className="text-[#DA1E28] hover:opacity-70"><Trash2 size={15} /></button>
                          </td>
                        </tr>
                      ))}
                      {shown.length === 0 && <tr><td colSpan={5} className="py-6 text-center opacity-50">{isMain ? "No messages waiting in the main inbox." : "No messages in this topic box yet."}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </Card>
      ) : (
      <Card>
        <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
          <span data-testid="audience-count">{data.count} record(s)</span>
          {data.duplicates > 0 && <Badge color="warning">{data.duplicates} duplicate email(s)</Badge>}
          <label className="ms-auto flex items-center gap-2">
            <ArrowDownUp size={14} className="opacity-60" />
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="audience-sort"
              className="rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1 text-sm">
              <option value="created_at">Date added</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
            </select>
            <select value={order} onChange={(e) => setOrder(Number(e.target.value))} data-testid="audience-order"
              className="rounded-md border border-black/15 dark:border-white/15 bg-transparent px-2 py-1 text-sm">
              <option value={-1}>Desc</option>
              <option value={1}>Asc</option>
            </select>
          </label>
          <Button variant="subtle" onClick={dedupe} disabled={busy} data-testid="audience-dedupe"><Copy size={14} /> Remove duplicates</Button>
        </div>

        <div className="mb-4 flex flex-wrap items-end gap-2">
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="audience-add-name" className="max-w-[200px]" />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="audience-add-email" className="max-w-[240px]" />
          <Button onClick={add} data-testid="audience-add-btn">Add</Button>
        </div>
        {msg && <p className="mb-3 text-sm opacity-70" data-testid="audience-msg">{msg}</p>}

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audience-table">
            <thead className="text-start opacity-60">
              <tr className="border-b border-black/10 dark:border-white/10">
                <th className="py-2 pe-4">Name</th><th className="py-2 pe-4">Email</th>
                <th className="py-2 pe-4">Added</th><th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id} className="border-b border-black/5 dark:border-white/5" data-testid={`audience-row-${r.id}`}>
                  <td className="py-2 pe-4">{r.name || "—"}</td>
                  <td className="py-2 pe-4">{r.email} {r.duplicate && <Badge color="warning">dup</Badge>}
                    {list === "newsletter" && r.subscribed === false && <Badge color="danger">unsubscribed</Badge>}</td>
                  <td className="py-2 pe-4 opacity-60">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="py-2 text-end">
                    {list === "newsletter" && r.subscribed !== false && (
                      <button onClick={() => unsub(r)} data-testid={`audience-unsub-${r.id}`}
                        className="me-3 text-xs text-[var(--primary)] hover:opacity-70">Unsubscribe</button>
                    )}
                    <button onClick={() => del(r.id)} data-testid={`audience-del-${r.id}`} className="text-[#DA1E28] hover:opacity-70"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={4} className="py-6 text-center opacity-50">No records yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      )}

      <ContentManager />
      <TranslationHealth />
    </div>
  );
}
